import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAuth } from '../context/AuthContext';
import {
  runPortfolio, keysFor, fmtCurrency, fmtPct, fmtRatio, bindingConstraint,
  PORTFOLIO_STRATEGIES, PORTFOLIO_SYMBOLS, PORTFOLIO_DEFAULT_ACCOUNT,
} from '../utils/portfolioApi';

/**
 * THE PORTFOLIO MANAGER: one book, several strategies, and an explicit funding list.
 *
 * <h2>The funding list is the control, not a filter on the output</h2>
 *
 * Every selected symbol x strategy is REPLAYED; the checkboxes decide which of them get CAPITAL.
 * An unfunded key still raises its signals, still accrues its own paper curve, and still appears
 * in the table with its standalone result — because "what would it have done" is the research
 * question and an absent row cannot answer it.
 *
 * <p>That distinction is why `CTS@QQQ` is unticked by default: it lost 161,432 standalone while
 * first-come-first-served funded 168 of its 181 trades. Dropping it took the measured book from
 * +62.68% at 31.39% drawdown to +94.78% at 16.35%.
 *
 * <h2>The two decline counts are never summed</h2>
 *
 * Cash and risk are separate columns and a sentence names which one is binding. A tight stop turns
 * a 1% risk budget into a position averaging 75-85% of equity, so the book runs out of cash long
 * before it runs out of risk appetite — across 585 measured signals the 6% ceiling refused exactly
 * zero. One combined "declined" number would send a reader to raise a limit that is not binding.
 *
 * <h2>Presentational</h2>
 *
 * Every derivation and every format is in `portfolioApi.js` and tested there. jsdom cannot render
 * this dialog without mocking MUI and the API away, at which point the test asserts the mocks.
 */

const MONO = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };
const GAIN = '#2e7d32';
const LOSS = '#c62828';

/** One headline figure. */
function Metric({ label, value, colour, hint }) {
  return (
    <Tooltip title={hint || ''} placement="top">
      <Box sx={{ minWidth: 142, px: 1.5, py: 1 }}>
        <Typography sx={{ fontSize: 11.5, letterSpacing: 0.5, opacity: 0.7 }}>
          {label}
        </Typography>
        <Typography
          sx={{ ...MONO, fontSize: 19, fontWeight: 700, lineHeight: 1.3 }}
          style={colour ? { color: colour } : undefined}
        >
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
}

Metric.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  colour: PropTypes.string,
  hint: PropTypes.string,
};

export default function DynaManPortfolioDialog({ open, onClose }) {
  const { api } = useAuth();

  const [symbols, setSymbols] = useState(PORTFOLIO_SYMBOLS);
  const [strategies, setStrategies] = useState(PORTFOLIO_STRATEGIES);
  const [account, setAccount] = useState(PORTFOLIO_DEFAULT_ACCOUNT);

  /**
   * The funding list, by key.
   *
   * <p>Seeded with the measured curation: everything except `CTS@QQQ`. A default of "fund
   * everything" would open on the configuration the measurement rejected, and a default of
   * "fund nothing" would open on a book that cannot trade.
   */
  const allKeys = useMemo(() => keysFor(symbols, strategies), [symbols, strategies]);
  const [unfunded, setUnfunded] = useState(() => new Set(['CTS@QQQ']));
  const fundKeys = useMemo(() => allKeys.filter((k) => !unfunded.has(k)), [allKeys, unfunded]);

  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const toggleIn = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    setResult(null);
  };

  const toggleFunding = (key) => {
    setUnfunded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setResult(null);
  };

  const run = async () => {
    setError('');
    setRunning(true);
    try {
      setResult(await runPortfolio(api, {
        symbols,
        strategies,
        // The ARRAY is always sent, even when empty: an absent list means "fund everything" on the
        // server and an empty one means "fund nothing". Collapsing them would make a cleared list
        // silently produce a fully-funded book.
        fundKeys,
        ...account,
      }));
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'The portfolio run failed');
    } finally {
      setRunning(false);
    }
  };

  const netColour = result && result.netPnl >= 0 ? GAIN : LOSS;
  const binding = bindingConstraint(result);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      data-testid="portfolio-dialog">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, flex: 1 }}>
          Portfolio Manager
        </Typography>
        <Chip label="temporal priority" size="small" sx={{ fontSize: 11 }} />
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* ── what to replay ──────────────────────────────────────────────────────────────── */}
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, opacity: 0.7 }}>
          REPLAY
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 11, opacity: 0.7 }}>Symbols</Typography>
            {PORTFOLIO_SYMBOLS.map((s) => (
              <FormControlLabel key={s} control={(
                <Checkbox size="small" checked={symbols.includes(s)}
                  onChange={() => toggleIn(symbols, setSymbols, s)} />
              )} label={<Typography sx={{ fontSize: 13 }}>{s}</Typography>} />
            ))}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, opacity: 0.7 }}>Strategies</Typography>
            {PORTFOLIO_STRATEGIES.map((s) => (
              <FormControlLabel key={s} control={(
                <Checkbox size="small" checked={strategies.includes(s)}
                  onChange={() => toggleIn(strategies, setStrategies, s)} />
              )} label={<Typography sx={{ fontSize: 13 }}>{s}</Typography>} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', pt: 2 }}>
            {[
              ['capital', 'Capital'],
              ['riskPerTradePct', 'Risk %/trade'],
              ['commissionPerShare', '$/share'],
              ['slippageBps', 'Slippage bps'],
            ].map(([field, label]) => (
              <TextField
                key={field}
                label={label}
                size="small"
                value={account[field]}
                onChange={(e) => {
                  const raw = e.target.value;
                  const n = Number(raw);
                  // Kept as typed while it is being typed; only a finite number is committed, so
                  // a half-entered value cannot be sent as NaN and 400 the run.
                  setAccount((a) => ({ ...a, [field]: raw === '' ? '' : (Number.isFinite(n) ? n : a[field]) }));
                  setResult(null);
                }}
                sx={{ width: 118, '& input': { ...MONO, fontSize: 13 } }}
              />
            ))}
          </Box>
        </Box>

        {/* ── the funding list ────────────────────────────────────────────────────────────── */}
        <Divider sx={{ my: 1.5 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, opacity: 0.7 }}>
          FUNDING LIST — ticked keys get capital; unticked keys are still replayed and reported
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {allKeys.length === 0 && (
            <Typography sx={{ fontSize: 13, opacity: 0.7, py: 1 }}>
              Pick at least one symbol and one strategy.
            </Typography>
          )}
          {allKeys.map((key) => (
            <FormControlLabel key={key} control={(
              <Checkbox size="small" checked={!unfunded.has(key)}
                data-testid={`fund-${key}`}
                onChange={() => toggleFunding(key)} />
            )} label={<Typography sx={{ ...MONO, fontSize: 13 }}>{key}</Typography>} />
          ))}
        </Box>
        {allKeys.length > 0 && fundKeys.length === 0 && (
          <Alert severity="info" sx={{ fontSize: 12.5, mb: 1 }}>
            Nothing is funded. The run will price every signal and back none of them — which is a
            legitimate measurement, not an error: each strategy&apos;s standalone column and paper
            drawdown are still computed.
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={run}
          disabled={running || allKeys.length === 0}
          startIcon={running ? <CircularProgress size={16} /> : <PlayArrowIcon />}
          data-testid="portfolio-run"
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {running ? 'Replaying…' : `Run portfolio (${allKeys.length} keys)`}
        </Button>

        {error && <Alert severity="error" sx={{ mt: 1.5, fontSize: 13 }}>{error}</Alert>}

        {/* ── the result ──────────────────────────────────────────────────────────────────── */}
        {result && (
          <Box sx={{ mt: 2 }} data-testid="portfolio-result">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}>
              <Metric label="ENDING EQUITY" value={fmtCurrency(result.endingEquity)}
                hint="Realised only. An open premise is a mark, not a result." />
              <Metric label="NET P&L" value={fmtCurrency(result.netPnl)} colour={netColour} />
              <Metric label="RETURN" value={fmtPct(result.returnPercent)} colour={netColour} />
              <Metric label="MAX DRAWDOWN" value={fmtPct(result.maxDrawdownPercent)}
                colour={LOSS}
                hint="Of the trade-sequence equity curve, so it is path-dependent." />
              <Metric label="PROFIT FACTOR" value={fmtRatio(result.profitFactor)}
                hint="n/a until something has lost. An absent denominator is not a perfect score." />
              <Metric label="RETURN / DD" value={fmtRatio(result.returnOverDrawdown)}
                hint="The scale-free reading: made more, or made more per unit of pain?" />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', mt: 0.5 }}>
              <Metric label="SIGNALS" value={result.signalsSeen} />
              <Metric label="FUNDED" value={result.admitted} colour={GAIN} />
              <Metric label="NO CASH" value={result.declinedExposure} colour={LOSS}
                hint="Refused for buying power — the budget that usually binds." />
              <Metric label="RISK REFUSED" value={result.declinedRisk}
                hint="Refused by the portfolio risk ceiling. Expect zero." />
              <Metric label="NOT FUNDED" value={result.declinedNotFunded}
                hint="A standing decision from the funding list, not a shortage." />
              <Metric label="CASH STARVED" value={fmtPct(result.cashStarvedPercent)} />
            </Box>

            {binding && (
              <Alert severity={result.declinedRisk === 0 && result.declinedExposure > 0
                ? 'warning' : 'info'} sx={{ mt: 1, fontSize: 12.5 }}>
                {binding}
              </Alert>
            )}

            {(result.warnings || []).map((w, i) => (
              <Alert key={i} severity="warning" sx={{ mt: 1, fontSize: 12.5 }}>{w}</Alert>
            ))}

            <Table size="small" sx={{ mt: 1.5 }}>
              <TableHead>
                <TableRow>
                  {['KEY', 'FUNDED?', 'SIGNALS', 'BACKED', 'NO CASH', 'NOT FUNDED',
                    'CONTRIBUTED', 'ALONE', 'PAPER DD'].map((h) => (
                      <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, opacity: 0.75 }}>
                        {h}
                      </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(result.strategies || []).map((row) => (
                  <TableRow key={row.key}
                    sx={{ opacity: row.inFundingList ? 1 : 0.6 }}>
                    <TableCell sx={{ ...MONO, fontSize: 13, fontWeight: 600 }}>{row.key}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.inFundingList ? 'funded' : 'measured'}
                        size="small"
                        sx={{
                          height: 20, fontSize: 10.5,
                          bgcolor: row.inFundingList ? 'rgba(46,125,50,0.16)'
                            : 'rgba(0,0,0,0.08)',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...MONO, fontSize: 13 }}>{row.signals}</TableCell>
                    <TableCell sx={{ ...MONO, fontSize: 13 }}>{row.funded}</TableCell>
                    <TableCell sx={{ ...MONO, fontSize: 13 }}>{row.noCash}</TableCell>
                    <TableCell sx={{ ...MONO, fontSize: 13 }}>{row.notFunded}</TableCell>
                    <TableCell sx={{
                      ...MONO, fontSize: 13,
                      color: row.contributed >= 0 ? GAIN : LOSS,
                    }}>
                      {fmtCurrency(row.contributed)}
                    </TableCell>
                    <TableCell sx={{
                      ...MONO, fontSize: 13,
                      color: row.standaloneNetPnl >= 0 ? GAIN : LOSS,
                    }}>
                      {fmtCurrency(row.standaloneNetPnl)}
                    </TableCell>
                    <TableCell sx={{ ...MONO, fontSize: 13 }}>
                      {fmtPct(row.paperDrawdown)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* THE CAVEAT BESIDE THE NUMBER IT QUALIFIES, not left to be inferred. */}
            <Typography sx={{ fontSize: 12, opacity: 0.8, mt: 1.5 }}>
              <b>ALONE</b> is each key on the WHOLE capital, so the column does not add up: the{' '}
              {result.standaloneCapitalMultiple} rows would need{' '}
              {result.standaloneCapitalMultiple}&times; the money
              ({fmtCurrency(result.sumStandaloneNetPnl)} in total). <b>CONTRIBUTED</b> is realised
              dollars on the SHARED equity path, so an early winner enlarged every later position
              and these are not independent either. Policy: {result.policy}. Replayed in{' '}
              {result.elapsedMillis} ms on daily bars.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

DynaManPortfolioDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
